var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
/**
 * This is a custom functional component that wraps Typography component with additional Tooltip.
 * Tooltip will only be displayed when text overflow occurs.
 * Tricky part is mobile - by default tooltip is shown after long touch (750ms) and touch event is bubbled up,
 * hence if there is click even handler in parent component, it will be triggered - unless touch will be released somewhere outside parent component.
 *
 * @param {object} props - Typography props.
 * @param {React.ReactNode} props.children - Content that should go inside Typography component.
 *
 * @example
 * <TypographyWithOverflow variant="body1">
 *  This is some long text that will eventually truncate
 * </TypographyWithOverflow>
 *
 * @returns {ReactElement} Returns Typography component wrapped with Tooltip.
 */
export const TypographyWithOverflow = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    const ref = useRef(null);
    const [tooltipEnabled, setTooltipEnabled] = useState(false);
    const compareSize = useCallback(() => {
        if (ref.current) {
            const compare = ref.current.scrollWidth > ref.current.clientWidth;
            setTooltipEnabled(compare);
        }
    }, []);
    useEffect(() => {
        compareSize();
        window.addEventListener('resize', compareSize);
        return () => window.removeEventListener('resize', compareSize);
    }, [compareSize]);
    return (React.createElement(Tooltip, { title: children, disableHoverListener: !tooltipEnabled, disableTouchListener: !tooltipEnabled },
        React.createElement(Typography, Object.assign({ ref: ref, noWrap: true, overflow: 'hidden', textOverflow: 'ellipsis' }, props), children)));
};
